import type { UpdateSystem } from "@engine/core/Lifecycle";
import type { Entity } from "../components";
import type { EcsWorld } from "../world";

/**
 * Simple wander AI: NPC picks a random point inside its home radius,
 * walks toward it, idles, then picks a new one.
 */
export class WanderSystem implements UpdateSystem {
  private query;

  constructor(world: EcsWorld) {
    this.query = world.with("position", "velocity", "wander").without("asleep");
  }

  update(dt: number): void {
    for (const e of this.query) this.step(e, dt);
  }

  private step(e: Entity, dt: number): void {
    const pos = e.position;
    const vel = e.velocity;
    const w = e.wander;
    if (!pos || !vel || !w) return;

    if (w.state === "idle") {
      w.cooldown -= dt;
      vel.x = 0;
      vel.y = 0;
      if (w.cooldown <= 0) {
        const angle = Math.random() * Math.PI * 2;
        const r = Math.random() * w.radius;
        w.target = {
          x: w.home.x + Math.cos(angle) * r,
          y: w.home.y + Math.sin(angle) * r,
        };
        w.state = "moving";
      }
      return;
    }

    const dx = w.target.x - pos.x;
    const dy = w.target.y - pos.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 4) {
      w.state = "idle";
      w.cooldown = 0.8 + Math.random() * 2.4;
      vel.x = 0;
      vel.y = 0;
      if (typeof e.heading === "number") e.heading = Math.atan2(dy, dx);
      return;
    }
    vel.x = (dx / dist) * w.speed;
    vel.y = (dy / dist) * w.speed;
    if (typeof e.heading === "number") e.heading = Math.atan2(dy, dx);
  }
}
